import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  CalendarCheck2,
  CalendarClock,
  BarChart3,
  LifeBuoy,
  Package,
  Boxes,
  Warehouse,
  Map,
  MapPinned,
  UsersRound,
  Send,
  PackageOpen,
  FileBarChart,
  Building2,
  BookOpen,
  FileText,
  Trash2,
  History,
  ChevronsLeft,
  Settings as SettingsIcon,
  Sparkles,
  X,
} from 'lucide-react';

import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/auth.store';
import type { Role } from '@/types/api';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  badge?: string;
  roles?: Role[];
}
interface NavSection {
  title: string;
  roles?: Role[];
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    title: 'Management',
    roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
    items: [
      { to: '/users', label: 'User Management', icon: Users, roles: ['OWNER', 'ADMIN'] },
      { to: '/areas', label: 'Area/Hub Management', icon: Map },
      { to: '/assign-area', label: 'Assign Area/Hub', icon: MapPinned },
    ],
  },
  {
    title: 'Inventory',
    roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
    items: [
      { to: '/inventory/items', label: 'Item Master', icon: Boxes },
      { to: '/inventory/stock', label: 'Item Stock', icon: Warehouse },
      { to: '/inventory/dispatches', label: 'Dispatch', icon: Send },
      { to: '/inventory', label: 'Stock Tracker', icon: Package },
      { to: '/inventory/returns', label: 'Returns to Main Store', icon: PackageOpen },
    ],
  },
  {
    title: 'Destroy',
    roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
    items: [
      { to: '/inventory/destroy', label: 'Destroy Item', icon: Trash2 },
      {
        to: '/inventory/destroy/history',
        label: 'Destruction History',
        icon: History,
      },
    ],
  },
  {
    title: 'Inventory Reports',
    roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
    items: [
      { to: '/inventory/reports/hub', label: 'Main Store Stock', icon: Building2 },
      { to: '/inventory/reports/area', label: 'Area/Hub-wise Stock', icon: Map },
      { to: '/inventory/reports/item', label: 'Item-wise Stock', icon: FileBarChart },
      { to: '/inventory/reports/usage', label: 'Usage History', icon: FileText },
      { to: '/inventory/reports/ledger', label: 'Item Ledger', icon: BookOpen },
    ],
  },
  {
    title: 'Customers',
    roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
    items: [{ to: '/customers', label: 'Customers', icon: UsersRound }],
  },
  {
    title: 'Support',
    items: [{ to: '/tickets', label: 'Tickets', icon: LifeBuoy }],
  },
  {
    title: 'Attendance',
    items: [
      {
        to: '/attendance/me',
        label: 'My Attendance',
        icon: CalendarClock,
        roles: ['ADMIN', 'SUB_ADMIN', 'SUPPORT', 'AGENT', 'VENDOR'],
      },
      {
        to: '/attendance',
        label: 'Attendance Management',
        icon: CalendarCheck2,
        roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
      },
      {
        to: '/attendance/reports',
        label: 'Reports',
        icon: BarChart3,
        roles: ['OWNER', 'ADMIN', 'SUB_ADMIN'],
      },
    ],
  },
  {
    title: 'System',
    roles: ['OWNER'],
    items: [{ to: '/settings', label: 'Settings', icon: SettingsIcon }],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onCloseMobile,
}: SidebarProps) {
  const role = useAuthStore((s) => s.user?.role);
  const visibleSections = navSections
    .filter((s) => !s.roles || (role && s.roles.includes(role)))
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (it) => !it.roles || (role && it.roles.includes(role)),
      ),
    }))
    .filter((s) => s.items.length > 0);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      {/*
        Mobile backdrop. Driven purely by CSS opacity (not AnimatePresence) so
        it can never get "stuck" mounted: during navigation the route change
        re-renders the tree mid-exit and framer-motion would otherwise fail to
        remove it, leaving a blurred scrim over the page until a reload.
      */}
      <div
        onClick={onCloseMobile}
        aria-hidden={!mobileOpen}
        className={cn(
          'fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-300 lg:hidden',
          mobileOpen
            ? 'opacity-100 backdrop-blur-sm'
            : 'pointer-events-none opacity-0',
        )}
      />

      <motion.aside
        animate={{ width: collapsed ? 80 : 264 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-border/60 bg-white/80 backdrop-blur-xl lg:sticky lg:top-0 lg:translate-x-0',
          'transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand */}
        <div className="flex h-16 items-center justify-between gap-2 px-4">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary-500 via-primary-600 to-indigo-600 text-white shadow-primary">
              <Sparkles className="h-5 w-5" />
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-white" />
            </div>
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.2 }}
                  className="min-w-0"
                >
                  <p className="text-sm font-semibold leading-tight tracking-tight">
                    VM Digital
                  </p>
                  <p className="text-xs text-muted-foreground">Admin Console</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            onClick={onCloseMobile}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mx-4 mb-2 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Nav */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2 scrollbar-thin">
          {visibleSections.map((section) => (
            <div key={section.title}>
              <AnimatePresence initial={false}>
                {!collapsed && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80"
                  >
                    {section.title}
                  </motion.p>
                )}
              </AnimatePresence>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end
                    onClick={onCloseMobile}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                        isActive
                          ? 'text-primary-700'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.span
                            layoutId="sidebar-active"
                            className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-50 via-indigo-50 to-primary-50/50 ring-1 ring-primary-100"
                            transition={{
                              duration: 0.3,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                          />
                        )}
                        {isActive && (
                          <motion.span
                            layoutId="sidebar-active-bar"
                            className="absolute left-1.5 top-1/2 z-10 h-4 w-1 -translate-y-1/2 rounded-full bg-gradient-to-b from-primary-500 to-indigo-500"
                            transition={{
                              duration: 0.3,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                          />
                        )}
                        <item.icon
                          className={cn(
                            'relative z-10 h-[18px] w-[18px] shrink-0 transition-colors',
                            isActive ? 'text-primary-600' : '',
                          )}
                        />
                        <AnimatePresence initial={false}>
                          {!collapsed && (
                            <motion.span
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -8 }}
                              transition={{ duration: 0.18 }}
                              className="relative z-10 flex flex-1 items-center justify-between"
                            >
                              <span className="truncate">{item.label}</span>
                              {item.badge && (
                                <span className="ml-2 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                                  {item.badge}
                                </span>
                              )}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Collapse */}
        <div className="border-t border-border/60 p-3">
          <button
            onClick={onToggle}
            className={cn(
              'hidden w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:inline-flex',
              collapsed && 'justify-center',
            )}
          >
            <motion.span
              animate={{ rotate: collapsed ? 180 : 0 }}
              transition={{ duration: 0.25 }}
            >
              <ChevronsLeft className="h-4 w-4" />
            </motion.span>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </motion.aside>
    </>
  );
}
